import { Trade, TokenAmount, CurrencyAmount } from '@etcswapv2/sdk-legacy'
import { useCallback, useMemo } from 'react'
import { usePublicClient, useWalletClient } from 'wagmi'
import { type Address } from 'viem'
import { ROUTER_ADDRESS } from '../constants'
import { erc20Abi } from '../constants/abis'
import { useTokenAllowance } from '../data/Allowances'
import { Field } from '../state/swap/actions'
import { useTransactionAdder, useHasPendingApproval } from '../state/transactions/hooks'
import { computeSlippageAdjustedAmounts } from '../utils/prices'
import { useActiveWeb3React } from './index'
import { getChainById } from '../wagmi'

// Max uint256 for unlimited approval
const MAX_UINT256 = 2n ** 256n - 1n

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED
}

// returns a variable indicating the state of the approval and a function which approves if necessary or early returns
export function useApproveCallback(
  amountToApprove?: CurrencyAmount,
  spender?: string
): [ApprovalState, () => Promise<void>] {
  const { account, chainId } = useActiveWeb3React()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()

  // Get the token from the amount - handle both TokenAmount (has 'token') and CurrencyAmount (has 'currency')
  // Use property checks instead of instanceof to avoid module duplication issues
  const token = useMemo(() => {
    if (!amountToApprove) return undefined
    // TokenAmount has a 'token' property
    if ('token' in amountToApprove) {
      return (amountToApprove as TokenAmount).token
    }
    // CurrencyAmount has a 'currency' property - check if it's a token (has 'address')
    if ('currency' in amountToApprove && amountToApprove.currency && 'address' in amountToApprove.currency) {
      return amountToApprove.currency as TokenAmount['token']
    }
    return undefined
  }, [amountToApprove])
  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender)
  const pendingApproval = useHasPendingApproval(token?.address, spender)

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    // Check if native currency (no address) - use property check instead of reference equality
    // to avoid module duplication issues with ETHER constant
    const isNativeCurrency = !amountToApprove.currency || !('address' in amountToApprove.currency)
    if (isNativeCurrency) return ApprovalState.APPROVED
    // we might not have enough data to know whether or not we need to approve
    if (!currentAllowance) return ApprovalState.UNKNOWN

    // amountToApprove will be defined if currentAllowance is
    return currentAllowance.lessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, pendingApproval, spender])

  const addTransaction = useTransactionAdder()

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily')
      return
    }
    if (!token) {
      console.error('no token')
      return
    }

    if (!walletClient || !publicClient || !chainId) {
      console.error('wallet not connected')
      return
    }

    if (!amountToApprove) {
      console.error('missing amount to approve')
      return
    }

    if (!spender) {
      console.error('no spender')
      return
    }

    const chain = getChainById(chainId)
    if (!chain) {
      console.error('unsupported chain')
      return
    }

    const tokenAddress = token.address as Address
    const spenderAddress = spender as Address
    // Convert JSBI to bigint
    const amountAsBigInt = BigInt(amountToApprove.raw.toString())

    let useExact = false
    let estimatedGas: bigint

    try {
      // Try to estimate gas for max approval
      estimatedGas = await publicClient.estimateContractGas({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'approve',
        args: [spenderAddress, MAX_UINT256],
        account: account as Address
      })
    } catch {
      // general fallback for tokens who restrict approval amounts
      useExact = true
      estimatedGas = await publicClient.estimateContractGas({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'approve',
        args: [spenderAddress, amountAsBigInt],
        account: account as Address
      })
    }

    // Add 20% gas margin
    const gasLimit = (estimatedGas * 120n) / 100n

    try {
      const hash = await walletClient.writeContract({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'approve',
        args: [spenderAddress, useExact ? amountAsBigInt : MAX_UINT256],
        gas: gasLimit,
        chain,
        account: account as Address
      })

      addTransaction({ hash }, {
        summary: 'Approve ' + amountToApprove.currency.symbol,
        approval: { tokenAddress: token.address, spender: spender }
      })
    } catch (error) {
      console.debug('Failed to approve token', error)
      throw error
    }
  }, [approvalState, token, publicClient, walletClient, account, chainId, amountToApprove, spender, addTransaction])

  return [approvalState, approve]
}

// wraps useApproveCallback in the context of a swap
export function useApproveCallbackFromTrade(trade?: Trade, allowedSlippage = 0) {
  const { chainId } = useActiveWeb3React()
  const amountToApprove = useMemo(
    () => (trade ? computeSlippageAdjustedAmounts(trade, allowedSlippage)[Field.INPUT] : undefined),
    [trade, allowedSlippage]
  )
  // Only use V2 router - V1 was never deployed on ETC
  const routerAddress = chainId ? ROUTER_ADDRESS[chainId] : undefined
  return useApproveCallback(amountToApprove, routerAddress)
}
